import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CustomisedTertiaryButton, FlexRow, Img } from "../../constants/common/StyledGlobalConstants";
import { Grid, Row } from "@vds/grids";
import { useTranslation } from "react-i18next";
import downArrow from "../../assets/icons/filled-down-arrow.png";
import upArrow from "../../assets/icons/filled-up-arrow.png";

const UserDropDown = ({ logo, logout, fullname, accountname, showUserDropDown, setShowUserDropDown, ariaHiddenValue }) => {
  const { t } = useTranslation("loginTranslation");
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowUserDropDown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const StyledUserBox = styled.div`
    z-index: 3;
    width: 330px;
    height: 112px;
    background-color: white;
    border-radius: 0.25rem 0.25rem 0 0;
    color: #fff;
    padding: 24px 16px 24px 16px;
    position: absolute;
    top: 4.3rem;
    right: 1.5rem;
    background-clip: padding-box;
    box-shadow: -1px 1px 2px 1px rgba(0, 0, 0, 0.2);
    filter: drop-shadow(rgba(0, 0, 0, 0.3) 0 2px 5px);
    float: left;
    display: flex;
  `;

  const StyledText = styled.p`
    font-family: "neue-haas-grotesk-text";
    font-style: normal;
    font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "")};
    font-size: ${(props) => (props.fontSize ? props.fontSize : "")};
    color: ${(props) => (props.fontColor ? props.fontColor : "#000000")};
    margin: ${(props) => (props.fontMargin ? props.fontMargin : "")};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 14rem;
  `;

  const StyledLogOut = styled.div`
    width: 330px;
    height: 48px;
    background-color: #f2f2f2;
    border-radius: 0 0 0.25rem 0.25rem;
    color: #fff;
    display: flex;
    flex-direction: row-reverse;
    position: absolute;
    top: 7rem;
    right: 0;
    float: left;
    box-shadow: -1px 1px 2px 1px rgba(0, 0, 0, 0.2);
  `;

  const StyledUserTopArrow = styled.div`
    &::after {
      content: " ";
      position: absolute;
      height: 9px;
      right: 24px;
      top: -15px;
      border-top: none;
      border-right: 15px solid transparent;
      border-left: 15px solid transparent;
      border-bottom: 15px solid white;
    }
  `;

  const handleToggleDropdown = () => {
    setShowUserDropDown(!showUserDropDown);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setShowUserDropDown(!showUserDropDown);
    }
  };

  return (
    <div ref={wrapperRef} style={{ display: "flex" }}>
      <div 
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }} 
        role="button" 
        aria-hidden={ariaHiddenValue} 
        aria-label="Profile Icon, click enter to dropdown the list" 
        aria-expanded={showUserDropDown}
        tabIndex="0"
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
      >
        <FlexRow
          border="1.2px solid black"
          borderRadius="50%"
          width="2.5rem"
          height="2.5rem"
          alignItems="center"
          justifyContent="center"
          fontSize="0.875rem"
          alignSelf="center !important"
          font-weight="400"
        >
          {logo}
        </FlexRow>
        <Img height="0.5rem" marginLeft="4px" src={showUserDropDown ? upArrow : downArrow} aria-hidden="true" alt=""></Img>
      </div>
      {showUserDropDown && (
        <StyledUserBox>
          <StyledUserTopArrow></StyledUserTopArrow>

          <FlexRow
            border="1.2px solid black"
            borderRadius="50%"
            width="64px"
            height="64px"
            alignItems="center"
            justifyContent="center"
            fontSize="24px"
            alignSelf="center !important"
            font-weight="450"
            color="black"
            font-family="neue-haas-grotesk-display"
            font-style="normal"
          >
            {logo}
          </FlexRow>
          <Grid>
            <Row>
              <StyledText fontSize="16px" fontWeight="400" fontColor="black" fontMargin="15px 0 0 8px">
                {fullname}
              </StyledText>
            </Row>
            <Row>
              <StyledText fontSize="13px" fontWeight="400" fontColor="#747474" fontMargin="0 0 0 8px">
                {accountname}
              </StyledText>
            </Row>
          </Grid>
          <StyledLogOut>
            <CustomisedTertiaryButton 
              fontSize="12px" 
              onClick={logout} 
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  logout();
                }
              }} 
              buttonWidth="auto" 
              lineHeight="20px"
            >
              {t("LOG OUT")}
            </CustomisedTertiaryButton>
          </StyledLogOut>
        </StyledUserBox>
      )}
    </div>
  );
};

UserDropDown.propTypes = {
  logo: PropTypes.string.isRequired,
  logout: PropTypes.func.isRequired,
  fullname: PropTypes.string.isRequired,
  accountname: PropTypes.string.isRequired,
  showUserDropDown: PropTypes.bool.isRequired,
  setShowUserDropDown: PropTypes.func.isRequired,
  ariaHiddenValue: PropTypes.bool.isRequired,
  fontWeight: PropTypes.string,
  fontSize: PropTypes.string,
  fontColor: PropTypes.string,
  fontMargin: PropTypes.string
};

export default UserDropDown;
